import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { getScreenWidth, makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';
import { TABLET_L, TABLET_P } from '@/constants';
import { getContent } from '@/services/api';
import Video from '@/components/BrightcoveVideo';
import Markdown from '@/components/Markdown';

import {
  Wrapper,
  VideoWrapper,
  YoutubeSection,
  YoutubeSectionWrapper,
  ButtonWrapper,
  Button,
  PlaceholderWrapper,
  Hr,
} from '@/pages/StylePage/StylePage.style';
import StylesGallery from '@/pages/EditorialPage/StylesGallery/StylesGallery';

import ProductRecommendation from '../../components/ProductRecommendation/ProductRecommendation';
import StylesLooksCarousel from './StylesLooksCarousel/StylesLooksCarousel';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';
import Placeholder from '../../components/Placeholder';

/**
 *「クリエーション」画面 > 各シーズンページ > 各スライダーのリンク先
 */
class StylePage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      // USE PRODUCTS > カルーセル表示用配列
      productData: [],
      // EDITORIAL PAGE データ (COLLECTION 関連情報取得を目的とする)
      editorialData: {},
      // ブラウザ伸縮時に更新されたスクリーンサイズ (スタイル用)
      syncingScreenSize: null,
    };
  }

  componentWillMount = () => this.setInitData();

  componentDidMount() {
    // ブラウザの横幅をマウスなどによる伸縮に追随させた時に、スクリーンサイズが適切に更新されない場合があるため、リサイズイベントを走らせる
    window.addEventListener('resize', () => this.setState({ syncingScreenSize: getScreenWidth() }));
  }

  componentDidUpdate = prevProps => {
    if (this.props !== prevProps) this.setInitData();
  };

  /**
   * 初期ロード時に各データを取得する
   *   * reselect によるメモ化でキャッシュされたデータが、URL を直打ちした時に渡らないリスクを想定
   */
  setInitData = () => {
    const { data, locale } = this.props;

    if (locale && _.get(data, 'breadcrumbs')) {
      const prevPath = `/${locale}${data.breadcrumbs.Breadcrumbs2.path}`;
      // COLLECTION 関連に必要なデータを取得する
      getContent({ contentType: 'editorial', locale, slug: prevPath.split('/')[3] }).then(editorialData =>
        this.setState({ editorialData })
      );
    }

    if (data && _.get(data, 'productsUsed') && _.find(data.productsUsed, item => _.get(item, 'product'))) {
      // USE PRODUCTS > カルーセル表示用配列を作成する
      this.setState({
        productData: _.map(data.productsUsed, ({ product }) => product),
      });
    }
  };

  render() {
    // COLLECTION > Placeholder 横幅
    const collectionPlaceholderWidth = 200;

    const { data, locale, copy, screenSize } = this.props;
    const { productData, editorialData, syncingScreenSize } = this.state;
    // 商品カルーセルに表示する「さらに見る」表示用の key
    const learnMore = 'learn_more';

    return (
      <React.Fragment>
        {_.get(data, 'region') && (
          <Helmet
            link={[
              makeCanonicalTagObj(locale, `/style/${data.slug}`),
              ...makeHreflangTagArr(data.region, `/style/${data.slug}`),
            ]}
          />
        )}
        {_.get(data, 'metaData') && (
          <Helmet
            title={_.get(data.metaData, 'title')}
            meta={[
              {
                name: 'description',
                content: _.get(data.metaData, 'description'),
              },
              {
                name: 'keyword',
                content: _.get(data.metaData, 'keyword'),
              },
              // TODO: OGP タグは一旦保留 (2020年01月07日)
              // {
              //   property: 'og:url',
              //   content: _.get(data.metaData, 'url'),
              // },
              // {
              //   property: 'og:type',
              //   content: _.get(data.metaData, 'type'),
              // },
              // {
              //   property: 'og:title',
              //   content: _.get(data.metaData, 'title'),
              // },
              // {
              //   property: 'og:image',
              //   content: _.get(data.metaData, 'image.file.url'),
              // },
              // {
              //   property: 'og:description',
              //   content: _.get(data.metaData, 'description'),
              // },
              // {
              //   property: 'og:site_name',
              //   content: _.get(data.metaData, 'siteName'),
              // },
            ]}
          />
        )}
        <Wrapper>
          {/* IMAGE (画像が一枚の場合は静的表示、複数枚の場合はスライダー表示) */}
          <StylesLooksCarousel style={data} />

          {_.get(data, 'youtubeSection') && (
            <YoutubeSectionWrapper>
              <Markdown src={data.youtubeSection} Component={YoutubeSection} />
            </YoutubeSectionWrapper>
          )}

          {/* VIDEO (動画がない場合はトリツメのため、Placeholder は表示させない) */}
          {_.get(data, 'videos') && (
            <VideoWrapper>
              <Video {...data.videos[0]} />
            </VideoWrapper>
          )}

          {/* USE PRODUCTS データ取得されていない場合 (data.productsUsed の初期値を引き渡す) */}
          {!!(_.get(data, 'productsUsed') && !_.find(data.productsUsed, item => _.get(item, 'product'))) && (
            <ProductRecommendation relatedProducts={data.productsUsed} isStylePage />
          )}

          {/* USE PRODUCTS データ取得済みの場合 */}
          {!!(
            productData &&
            productData.length > 0 &&
            _.get(data, 'learnMore') &&
            _.get(data, 'productsUsed') &&
            _.get(data, 'productUseTitle') &&
            _.find(data.productsUsed, item => _.get(item, 'product'))
          ) && (
            <ProductRecommendation
              stylePageTitle={data.productUseTitle}
              relatedProducts={productData}
              variation={data.productsUsed}
              copy={{ [learnMore]: data.learnMore }}
              isStylePage
            />
          )}

          {/* COLLECTION */}
          {copy &&
            _.get(editorialData, 'styles') &&
            _.get(editorialData, 'season') &&
            _.get(editorialData, 'slug') &&
            _.get(editorialData, 'title') &&
            editorialData.styles.length > 0 && (
              <StylesGallery
                copy={copy}
                styles={editorialData.styles}
                season={editorialData.season}
                locale={locale}
                prevSlug={editorialData.slug}
                ancestor={editorialData.title.toUpperCase()}
              />
            )}

          {/* 「戻る」ボタン */}
          <ButtonWrapper height={(syncingScreenSize || screenSize) > TABLET_L ? '72px' : '53px'}>
            {!(locale && _.get(data, 'backButton') && _.get(data, 'breadcrumbs')) ? (
              <Placeholder w={(syncingScreenSize || screenSize) > TABLET_L ? '300px' : '220px'} h="100%" />
            ) : (
              <Link
                to={`/${locale}${data.breadcrumbs.Breadcrumbs2.path}`}
                href={`/${locale}${data.breadcrumbs.Breadcrumbs2.path}`}
              >
                <Button>{data.backButton}</Button>
              </Link>
            )}
          </ButtonWrapper>
        </Wrapper>
        {!(locale && data && _.get(data, 'name') && _.get(data, 'breadcrumbs')) ? (
          <Placeholder
            w="200px"
            h="24px"
            m={(syncingScreenSize || screenSize) > TABLET_L ? '0 0 20px 40px' : '0 0 20px 20px'}
          />
        ) : (
          <BreadCrumbMenu
            currentPath={`https://www.shiseido-professional.com/${locale}/style/${data.slug}`}
            items={[
              {
                name: 'TOP',
                path: `/${locale}/`,
              },
              {
                name: data.breadcrumbs.Breadcrumbs1.name,
                path: `/${locale}/inspiration/`,
              },
              {
                name: data.breadcrumbs.Breadcrumbs2.name,
                path: `/${locale}${data.breadcrumbs.Breadcrumbs2.path}`,
              },
              {
                name: data.name.toUpperCase(),
                path: null,
              },
            ]}
          />
        )}
      </React.Fragment>
    );
  }
}

StylePage.defaultProps = {
  copy: {},
  data: {
    notLoaded: true,
    colors: [{ key: 1 }, { key: 2 }],
    // デザイン調整のため、ブレイクポイントを TABLET_P (600px) で設定
    productsUsed: getScreenWidth() > TABLET_P ? [{ key: 1 }, { key: 2 }, { key: 3 }] : [{ key: 1 }],
  },
  screenSize: null,
};

StylePage.propTypes = {
  locale: PropTypes.string.isRequired,
  copy: PropTypes.shape({}),
  data: PropTypes.shape({}),
  // スクリーンサイズ
  screenSize: PropTypes.number,
};

const mapState = state => ({
  copy: state.siteOptions.siteCopyEditorialPage,
  // スクリーンサイズを実寸で取得する
  screenSize: getScreenWidth(),
});

export default connect(mapState)(StylePage);



// WEBPACK FOOTER //
// ./src/pages/StylePage/StylePage.js